import { api, handleApiError } from '../client'
import type {
  DataSource,
  DataSourceField,
  FieldBinding,
  CreateDataSourceRequest,
  UpdateDataSourceRequest,
  BindFieldRequest,
  DataSourceTestResult,
} from '../types'

export const dataSourceService = {
  /**
   * Gets all data sources. Optionally filters by tenant.
   */
  async getDataSources(tenantId?: string): Promise<DataSource[]> {
    try {
      const params = tenantId ? { tenantId } : undefined
      const response = await api.get<DataSource[]>('/v1/data-sources', { params })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets a specific data source by ID.
   */
  async getDataSource(id: string): Promise<DataSource> {
    try {
      const response = await api.get<DataSource>(`/v1/data-sources/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets all fields for a data source.
   */
  async getDataSourceFields(id: string): Promise<DataSourceField[]> {
    try {
      const response = await api.get<DataSourceField[]>(`/v1/data-sources/${id}/fields`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Creates a new custom data source.
   */
  async createDataSource(data: CreateDataSourceRequest): Promise<DataSource> {
    try {
      const response = await api.post<DataSource>('/v1/data-sources', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates an existing data source.
   */
  async updateDataSource(id: string, data: UpdateDataSourceRequest): Promise<DataSource> {
    try {
      const response = await api.put<DataSource>(`/v1/data-sources/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deletes a data source.
   */
  async deleteDataSource(id: string): Promise<void> {
    try {
      await api.delete(`/v1/data-sources/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Tests a data source connection.
   */
  async testDataSource(id: string): Promise<DataSourceTestResult> {
    try {
      const response = await api.post<DataSourceTestResult>(`/v1/data-sources/${id}/test`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Refreshes data from a data source.
   */
  async refreshDataSource(id: string): Promise<DataSource> {
    try {
      const response = await api.post<DataSource>(`/v1/data-sources/${id}/refresh`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Binds a template field to a data source field.
   */
  async bindField(templateId: string, fieldId: string, binding: BindFieldRequest): Promise<FieldBinding> {
    try {
      const response = await api.post<FieldBinding>(
        `/v1/templates/${templateId}/fields/${fieldId}/binding`,
        binding
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Unbinds a template field from its data source.
   */
  async unbindField(templateId: string, fieldId: string): Promise<void> {
    try {
      await api.delete(`/v1/templates/${templateId}/fields/${fieldId}/binding`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets all field bindings for a template.
   */
  async getTemplateBindings(templateId: string): Promise<FieldBinding[]> {
    try {
      const response = await api.get<FieldBinding[]>(`/v1/templates/${templateId}/bindings`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets a preview value for a bound field.
   */
  async getFieldPreview(dataSourceId: string, fieldKey: string): Promise<{ value: string }> {
    try {
      const response = await api.get<{ value: string }>(
        `/v1/data-sources/${dataSourceId}/fields/${fieldKey}/preview`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
